import React, { useEffect, useState } from 'react';
import { triggerEvent } from '@/utils/helpers';

interface IPortalBoxProps {
  boxId: string;
  readyEventName: string;
}

/**
 * This hooks returns the HtmlElement once it is completed ready and present in the document
 * @param boxId - the container id to be verified
 * @param readyEventName - the event to be listed once it is ready (the event should be emitted by the component)
 */
export const usePortalBoxHandler = ({ boxId, readyEventName }: IPortalBoxProps) => {
  const [elementPortalBox, setElementPortalBox] = useState<Element | null>();

  useEffect(() => {
    const handlePortalBoxReady = () => {
      const elementRef = document.getElementById(boxId);
      setElementPortalBox(elementRef);
    };
    handlePortalBoxReady();
    document.addEventListener(readyEventName, handlePortalBoxReady);
    return () => document.removeEventListener(readyEventName, handlePortalBoxReady);
  }, [setElementPortalBox, boxId, readyEventName]);

  return {
    elementPortalBox,
  };
};

/**
 * This HoC attaches to and specific element the event triggering on loaded in order to emmit the readyEvent
 * @param Component
 * @param boxId
 * @param readyEventName
 */
export const withPortalBoxFeatures =
  (Component: React.ComponentType<any>, { boxId, readyEventName }: IPortalBoxProps) =>
  (props?: any) => {
    useEffect(() => {
      triggerEvent({ target: document, eventName: readyEventName });
      return () => triggerEvent({ target: document, eventName: readyEventName });
    }, []);

    return <Component {...props} id={boxId} />;
  };
